/**
 * RESPONSIBILITY: the @Choice column a row asked to draw as radios or checkboxes.
 *
 * Shares the view with the full-size control, so a column and a field declaring the same
 * `as` cannot end up looking like two different things.
 *
 * The radios are grouped by row index as well as key: without it, ticking "Yes" on the
 * third row would clear it on the first, because every radio on the page would share one
 * name and the browser treats that as one group.
 */
import { resolveOptions } from '@framework/core';
import { ChoiceGroup } from './choice-group';
import { str, type Cell } from './cell-types';

const ChoiceGroupCell: Cell['render'] = ({ field, value, onChange, id, index, root, row }) => {
  const allowed = field.in
    ? resolveOptions(field.in, root, row)
    : [...(field.fixed ?? [])];

  const multiple = Boolean(field.multiple);
  const held: string[] = multiple
    ? (Array.isArray(value) ? (value as string[]) : [])
    : (value ? [str(value)] : []);

  if (allowed.length === 0) {
    return <span className="ctl-hint">{field.in} is empty</span>;
  }

  return (
    <ChoiceGroup
      options={allowed} held={held} multiple={multiple}
      name={`${field.key}-${index}`} id={id}
      onToggle={(v) => onChange(multiple
        ? (held.includes(v) ? held.filter((x) => x !== v) : [...held, v])
        : v)}
    />
  );
};

export const choiceGroupCell: Cell = { render: ChoiceGroupCell, basis: () => '100%' };
